import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/brand/BrandLogo";
import CTAButton from "@/components/app/CTAButton";
import { Compass } from "lucide-react";

export default function Register() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [pseudo, setPseudo] = useState("");
    const [password, setPassword] = useState("");
    const [roles, setRoles] = useState({ buyer: true, seller: false });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        const selected = Object.entries(roles).filter(([, v]) => v).map(([k]) => k);
        if (selected.length === 0) {
            setError("Choisis au moins un rôle.");
            return;
        }
        setLoading(true); setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password, pseudo, roles: selected });
            setUser(data);
            navigate("/app", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    }

    const roleCard = (key, title, desc) => {
        const active = roles[key];
        return (
            <button
                type="button"
                key={key}
                data-testid={`register-role-${key}`}
                onClick={() => setRoles((r) => ({ ...r, [key]: !r[key] }))}
                className={`block w-full rounded-xl border p-4 text-left transition-colors ${
                    active
                        ? "border-md-primary bg-md-primary/5"
                        : "border-md-border bg-md-bg hover:bg-md-surface-2"
                }`}
            >
                <p className="md-overline">{active ? "Activé" : "Désactivé"}</p>
                <p className="mt-1 font-semibold text-md-text">{title}</p>
                <p className="mt-1 text-xs text-md-text-2">{desc}</p>
            </button>
        );
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-md-bg px-4 py-12">
            <div className="w-full max-w-md">
                <div className="mb-8 flex justify-center"><BrandLogo variant="horizontal" size="md" /></div>
                <div className="rounded-2xl border border-md-border bg-md-surface p-6 sm:p-8">
                    <h1 className="md-h2 text-md-text">Rejoindre l’aventure</h1>
                    <p className="mt-2 text-sm text-md-text-2">
                        Crée ton compte de dresseur. Tu pourras chercher des cartes, en proposer, ou les deux.
                    </p>
                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {roleCard("buyer", "Chercheur", "Lance des missions pour compléter ton classeur.")}
                            {roleCard("seller", "Dresseur", "Fouille tes doubles et propose des cartes.")}
                        </div>
                        <div>
                            <label className="md-overline mb-2 block">Pseudo</label>
                            <input
                                type="text"
                                required
                                minLength={2}
                                maxLength={32}
                                value={pseudo}
                                onChange={(e) => setPseudo(e.target.value)}
                                data-testid="register-pseudo"
                                className="block w-full rounded-lg border border-md-border bg-md-bg px-3 py-2.5 text-sm text-md-text outline-none focus:border-md-primary"
                            />
                        </div>
                        <div>
                            <label className="md-overline mb-2 block">Email</label>
                            <input
                                type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="register-email"
                                className="block w-full rounded-lg border border-md-border bg-md-bg px-3 py-2.5 text-sm text-md-text outline-none focus:border-md-primary"
                            />
                        </div>
                        <div>
                            <label className="md-overline mb-2 block">Mot de passe</label>
                            <input
                                type="password" required minLength={8} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="register-password"
                                className="block w-full rounded-lg border border-md-border bg-md-bg px-3 py-2.5 text-sm text-md-text outline-none focus:border-md-primary"
                            />
                            <p className="mt-1 text-xs text-md-text-2">Minimum 8 caractères.</p>
                        </div>
                        {error && (
                            <div className="rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger" data-testid="register-error">
                                {error}
                            </div>
                        )}
                        <CTAButton type="submit" size="block" loading={loading} icon={<Compass className="h-4 w-4" />} testId="register-submit">
                            Créer mon compte
                        </CTAButton>
                    </form>
                    <p className="mt-6 text-center text-sm text-md-text-2">
                        Déjà membre ?{" "}
                        <Link to="/login" className="font-semibold text-md-text underline-offset-2 hover:underline">
                            Connecte-toi
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
